import { create } from 'zustand';
import { supabase, DBOwner } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';
import { useInventoryStore } from './inventoryStore';

export interface Owner {
  id: string;
  name: string;
  notes: string | null;
  createdAt: Date;
}

interface OwnersState {
  owners: Owner[];
  isLoading: boolean;
  error: string | null;

  fetchOwners: () => Promise<void>;
  addOwner: (name: string, notes?: string) => Promise<Owner | null>;
  updateOwner: (id: string, patch: { name?: string; notes?: string }) => Promise<void>;
  removeOwner: (id: string) => Promise<void>;
}

const toOwner = (row: DBOwner): Owner => ({
  id: row.id,
  name: row.name,
  notes: row.notes,
  createdAt: new Date(row.created_at),
});

// Guards against a stale/concurrent fetch leaving isLoading stuck true.
let ownersFetchGen = 0;

export const useOwnersStore = create<OwnersState>((set, get) => ({
  owners: [],
  isLoading: false,
  error: null,

  fetchOwners: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ owners: [], isLoading: false });
      return;
    }

    const gen = ++ownersFetchGen;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('owners')
        .select('*')
        .eq('collection_id', collectionId)
        .order('name', { ascending: true });

      if (gen !== ownersFetchGen) return; // a newer fetch superseded this one

      if (error) {
        set({ isLoading: false, error: error.message });
        return;
      }
      set({ owners: (data ?? []).map(toOwner), isLoading: false });
    } catch (err) {
      if (gen !== ownersFetchGen) return;
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load owners',
      });
    }
  },

  addOwner: async (name: string, notes = '') => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return null;

    const trimmed = name.trim();
    if (!trimmed) return null;

    set({ error: null });

    const { data, error } = await supabase
      .from('owners')
      .insert({ name: trimmed, notes: notes.trim() || null, collection_id: collectionId })
      .select()
      .single();

    if (error) {
      console.error('[addOwner]', error);
      set({ error: error.message });
      return null;
    }
    if (!data) return null;
    const owner = toOwner(data);
    set({ owners: [...get().owners, owner].sort((a, b) => a.name.localeCompare(b.name)) });
    return owner;
  },

  updateOwner: async (id: string, patch) => {
    const existing = get().owners.find(o => o.id === id);
    if (!existing) return;

    const oldName = existing.name;
    const updates: Record<string, string | null> = {};
    if (patch.name !== undefined) updates.name = patch.name.trim() || oldName;
    if (patch.notes !== undefined) updates.notes = patch.notes.trim() || null;

    const newName = updates.name;
    const nameChanged = patch.name !== undefined && newName !== oldName;

    const { data, error } = await supabase
      .from('owners')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) { set({ error: error.message }); return; }
    if (!data) return;

    set({ owners: get().owners.map(o => o.id === id ? toOwner(data) : o).sort((a, b) => a.name.localeCompare(b.name)) });

    if (nameChanged && newName) {
      const { error: unitsError } = await supabase
        .from('stock_units')
        .update({ owner: newName })
        .eq('owner', oldName);

      if (unitsError) {
        set({ error: unitsError.message });
        return;
      }

      useInventoryStore.setState(s => ({
        stockUnits: s.stockUnits.map(u =>
          u.owner === oldName ? { ...u, owner: newName } : u
        ),
      }));
    }
  },

  removeOwner: async (id: string) => {
    const { error } = await supabase.from('owners').delete().eq('id', id);
    if (error) { set({ error: error.message }); return; }
    set({ owners: get().owners.filter(o => o.id !== id) });
  },
}));
